import { t } from "i18next";
import { useState } from "react";
import { Trans } from "react-i18next";
import { useNavigate } from "react-router-dom";

import nftPlaceholder from "@/assets/icons/nft.svg";
import Parachute from "@/assets/icons/parachute.svg?react";
import Warning from "@/assets/icons/warning.svg?react";
import level0 from "@/assets/images/level-0.png";
import evmIcon from "@/assets/icons/networks/evm.svg";
import { Achievements } from "@/components/Achievements";
import { AvailableAchievement } from "@/components/AvailableAchievement";
import { WarningBlock } from "@/components/WarningBlock";
import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Levels } from "@/components/Levels";
import { Networks } from "@/components/Networks";
import NFTMedia from "@/components/NFTMedia";
import { Rating } from "@/components/Rating";
import { ReMintWithExpiry } from "@/components/ReMintWithExpiry";
import { Score } from "@/components/Score";
import { Stars } from "@/components/Stars";
import { DEFAULT_SCORE_FOR_MINT_NFT, LEAR_MORE } from "@/constants";
import { useTrustScore } from "@/hooks/api/useTrustScore";
import { useStore } from "@/hooks/store";
import { useLevelActions } from "@/hooks/useLevelActions";
import { MIXPANEL_EVENTS } from "@/interfaces/mixpanel";
import { Network } from "@/interfaces/network";
import { useModalContext } from "@/providers/modal-provider/context";
import { APP_ROUTES } from "@/routes/constants";
import { logger } from "@/services/config/mixpanel";
import { getLevels } from "@/utils/levels";

import styles from "./styles";

export const Home: React.FC = () => {
  const navigate = useNavigate();
  const { user, network, currentNetworkData, userLoading } = useStore((store) => ({
    user: store.user,
    network: store.network,
    currentNetworkData: store.user.networksData[store.network],
    networkData: store.user.networksData,
    manualScoreUpdate: store.manualScoreUpdate,
    userLoading: store.userLoading,
  }));
  const modal = useModalContext();

  const retrievedScore = useTrustScore();
  const { minting, mintingNFT, reMinting, reMint, mint, mintNFT, readNews, connectWallet } = useLevelActions(
    retrievedScore.mutate,
  );

  const [achievementsIsOpen, setAchievementsIsOpen] = useState(false);

  const levelsData = getLevels({
    mint: {
      handler: mint,
      loading: minting,
      disabled: retrievedScore.data === undefined || retrievedScore.data.trust === 0 || userLoading,
    },
    mintNFT: {
      handler: mintNFT,
      loading: mintingNFT,
      isSoldOut: Boolean(currentNetworkData.nft?.isSoldOut),
      isPurchased: Boolean(currentNetworkData.nft?.isPurchased),
      minAllowedScore: currentNetworkData.nft?.minAllowedScore || DEFAULT_SCORE_FOR_MINT_NFT,
    },
    userLevel: currentNetworkData.level,
    userScore: currentNetworkData?.score?.value,
    openLevelModal: modal?.level?.openModal,
    network,
    news: {
      url: user.news,
      action: readNews,
    },
    socials: {
      x: user.x,
      telegram: user.telegram,
    },
  });

  const userLevelData = levelsData[currentNetworkData.level === 0 ? 0 : currentNetworkData.level - 1];

  const isExpiredScore = currentNetworkData?.score?.expirationDate
    ? Date.now() > currentNetworkData?.score?.expirationDate
    : false;
  const isShining = retrievedScore.data && retrievedScore.data.trust > currentNetworkData?.score?.value;

  return (
    <styles.HomeContainer>
      <Stars />
      <styles.TitleDescription>
        <Header />
        <styles.Title>{t("Home.HapiScore.Title")}</styles.Title>
        <styles.Description>{t("Home.HapiScore.Description")}</styles.Description>
        <styles.LearMore
          onClick={() => {
            logger.trackEvent(MIXPANEL_EVENTS.LEARN_MORE, { link: LEAR_MORE });
            window.open(LEAR_MORE);
          }}
        >
          {t("Actions.LearnMore")}
        </styles.LearMore>
      </styles.TitleDescription>
      {Boolean(user.totalScore) && (
        <styles.TotalScore>
          <p>{t("Home.TotalTrustScore")}</p>
          <p>{user.totalScore}</p>
        </styles.TotalScore>
      )}
      <styles.GetTotalScoreContainer>
        <Button
          iconLeft={<img src={evmIcon} width={24} />}
          name={"MintScoreNetwork"}
          additionalDataForName={{ network: "Ethereum" }}
          handler={() => {
            logger.trackEvent(MIXPANEL_EVENTS.USER_ENTER_PROMOTION);
            navigate(APP_ROUTES.PROMOTION);
          }}
        />
      </styles.GetTotalScoreContainer>
      <Networks />
      {network === Network.STELLAR && <WarningBlock text={t("Messages.TrustScoreInTesting", { network })} />}
      {network === Network.BSC && <WarningBlock text={t("Messages.TemporaryProblems")} />}
      <Rating />
      {network === Network.STELLAR && !user.stellarAccount && connectWallet && (
        <styles.ConnectBlock>
          <Warning />
          <p>
            {" "}
            <Trans i18nKey="Home.ConnectNetwork" components={{ br: <br /> }} values={{ network }} />
          </p>
          <Button name={"Connect"} handler={connectWallet} />
        </styles.ConnectBlock>
      )}
      <styles.Score $isExpiredScore={isExpiredScore}>
        <Score
          score={(currentNetworkData.level === 0 ? retrievedScore.data?.trust : currentNetworkData?.score?.value) || 0}
          levelData={
            currentNetworkData?.level === 0
              ? {
                  img: level0,
                  level: currentNetworkData.level,
                }
              : {
                  img: userLevelData?.img ?? "",
                  level: currentNetworkData.level,
                }
          }
          loading={currentNetworkData.level === 0 ? retrievedScore.isLoading : false}
          isShining={Boolean(isShining)}
        />
      </styles.Score>
      <AvailableAchievement />
      <styles.Body>
        {currentNetworkData.level !== 0 && retrievedScore.data !== undefined && (
          <ReMintWithExpiry
            newScore={retrievedScore.data.trust}
            reMint={reMint}
            reMinting={reMinting}
            currentScore={currentNetworkData?.score?.value}
            expirationDate={currentNetworkData.score.expirationDate}
          />
        )}
        {Boolean(user.achievements?.length) && (
          <styles.UserDataRow $isOpen={achievementsIsOpen}>
            <div onClick={() => navigate(APP_ROUTES.AIRDROPS)}>
              {currentNetworkData.nft?.array.length ? (
                <styles.NFTContainer>
                  <NFTMedia media={currentNetworkData.nft.array[0].url} />
                  <styles.Lock>
                    <Parachute />
                  </styles.Lock>
                </styles.NFTContainer>
              ) : (
                <styles.EmptyNFTContainer>
                  <img src={nftPlaceholder} />
                  <styles.Lock>
                    <Parachute />
                  </styles.Lock>
                </styles.EmptyNFTContainer>
              )}
            </div>
            <styles.Achievements $isOpen={achievementsIsOpen}>
              <Achievements handler={setAchievementsIsOpen} />
            </styles.Achievements>
          </styles.UserDataRow>
        )}
        <Levels levels={levelsData} />
      </styles.Body>
    </styles.HomeContainer>
  );
};
